import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { beep } from "@/lib/sound";

// Generic support-notification poller for both agent (/agent/support) and admin (/admin/support).
// Plays a sound + fires a browser notification when the unread count increases.
export function useSupportNotifications(base, enabled = true, intervalMs = 12000) {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const prev = useRef(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get(`${base}/notifications`);
      const u = data.unread || 0;
      if (prev.current !== null && u > prev.current) {
        beep();
        const latest = (data.notifications || []).find((n) => !n.read);
        if (latest && "Notification" in window && Notification.permission === "granted") {
          try { new Notification(latest.title, { body: latest.message, tag: latest.ticket_id }); } catch { /* noop */ }
        }
      }
      prev.current = u;
      setUnread(u);
      setItems(data.notifications || []);
    } catch { /* silent */ }
  }, [base, enabled]);

  useEffect(() => {
    if (!enabled) return;
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    load();
    const t = setInterval(load, intervalMs);
    return () => clearInterval(t);
  }, [load, enabled, intervalMs]);

  const markRead = useCallback(async () => {
    try { await api.post(`${base}/notifications/read`); setUnread(0); prev.current = 0; load(); } catch { /* noop */ }
  }, [base, load]);

  return { unread, items, markRead, refresh: load };
}
